import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const maintenanceCosts = [
    {
        id: 'routine-costs',
        title: '1. Typical Routine Maintenance Costs',
        text: 'Annual maintenance usually includes predictable services like oil changes ($70-$150), tire rotations ($30-$80), and filter replacements ($30-$100). These base costs are what you should plan for every year to prevent major breakdowns.'
    },
    {
        id: 'vehicle-type',
        title: '2. How Vehicle Type Changes the Budget',
        text: 'Parts and labor vary by vehicle class. Larger SUVs and trucks have more expensive tires and higher oil capacities, while luxury or imported brands often require specialized fluids and have higher labor rates at dealerships.'
    },
    {
        id: 'unexpected',
        title: '3. Why Unexpected Repairs Change the Average',
        text: 'The yearly total jumps when wear items like brake pads ($250-$500 per axle) or tires ($600-$1,200 set) need replacement. Older cars are more likely to need cooling system parts or sensor repairs that aren\'t on the regular schedule.'
    },
    {
        id: 'budgeting',
        title: '4. How to Build a Realistic Yearly Budget',
        text: 'A practical budget combines routine service costs with a repair reserve. A good rule of thumb is to set aside $100-$150 per month, depending on your vehicle\'s age and mileage, to cover both planned and surprise visits.'
    }
];

const faqs = [
    {
        question: 'Does yearly maintenance cost include repairs?',
        answer: 'It depends on the definition. For budgeting, it is best to separate predictable "maintenance" (fluids/filters) from "repairs" (fixing what broke). Most drivers spend $500-$1,200 annually combining both.'
    },
    {
        question: 'Do older cars always cost more to maintain?',
        answer: 'Usually, yes. As parts wear out over time, the frequency of required repairs increases. However, some older models are simpler and cheaper to fix than newer, high-tech vehicles.'
    },
    {
        question: 'Should I budget monthly or yearly for maintenance?',
        answer: 'Saving a monthly "car fund" is easier for most people. Even if you don\'t spend it every month, it ensures you have the cash ready when big items like tires or brakes come due.'
    },
    {
        question: 'Why do two drivers with similar cars spend very different amounts?',
        answer: 'Mileage is the biggest factor, followed by climate and driving style. Heavy city driving or extreme temperatures accelerate wear on almost every component.'
    }
];

const Blog15 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 15;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            How Much Does Car Maintenance Cost Per Year in the USA? (2026 Guide)
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Annual car maintenance can range from several hundred to well over a thousand dollars. The final total depends on the age, mileage, and service history.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 7 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/15_1.jpeg"
                            alt="Car maintenance planning and service cost budgeting"
                            title="How Much Does Car Maintenance Cost Per Year in the USA?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: maintenance_cost_planning.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#routine-costs" className="text-gray-900 no-underline hover:underline">Typical routine costs</a></li>
                            <li><a href="#vehicle-type" className="text-gray-900 no-underline hover:underline">Cost by vehicle type</a></li>
                            <li><a href="#unexpected" className="text-gray-900 no-underline hover:underline">Unexpected repair impact</a></li>
                            <li><a href="#budgeting" className="text-gray-900 no-underline hover:underline">Building a budget</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Most drivers do not think much about maintenance costs until the car needs tires, brakes, a battery, and a service visit all in the same year. That is usually when the yearly total starts feeling much bigger than expected.
                        </p>
                        <p className="mb-0 text-gray-700">
                            There is no single number that fits every driver, but understanding the biggest yearly cost drivers—mileage, vehicle type, and service history—helps you build a realistic plan.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>What is the average yearly maintenance cost?</strong><br />
                            For most drivers in the USA, annual maintenance and repair costs range from $500 to $1,500. This includes both predictable routine services and occasional wear-item replacements.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/15_2.jpeg"
                            alt="Driver discussing service costs and repair planning"
                            title="Understanding annual maintenance budget"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: budget_discussion.png
                        </figcaption>
                    </figure>

                    {maintenanceCosts.map((cost) => (
                        <section key={cost.id} id={cost.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {cost.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{cost.text}</p>
                        </section>
                    ))}

                    <section id="budget-checklist" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Practical Budgeting Checklist
                        </h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Gather the last 12 months of maintenance invoices</li>
                            <li>Identify upcoming scheduled services in your owner's manual</li>
                            <li>Check tire tread depth and brake pad thickness</li>
                            <li>Set aside a monthly repair reserve based on vehicle age</li>
                            <li>Track actual vs. expected spend to refine your budget</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Maintenance spending gets easier to control when it is tracked systematically. CarFido Assistant takes the guesswork out of budgeting.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Provide personalized cost tracking based on your actual history</li>
                                <li>Compare expected vs. actual spend to catch budget drift early</li>
                                <li>Project future maintenance needs before they arrive</li>
                                <li>Help you decide if a repair quote is in line with national averages</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Good maintenance budgeting is less about guessing the exact number and more about reducing nasty surprises. By tracking your routine work and keeping a small repair buffer, you protect your wallet and your car.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Remember, the cheapest car to maintain is the one that gets its routine service done on time. Acting early saves money and ensures your car stays reliable year after year.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#routine-costs" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Routine costs</a></li>
                            <li><a href="#vehicle-type" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Vehicle type</a></li>
                            <li><a href="#unexpected" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Unexpected repairs</a></li>
                            <li><a href="#budget-checklist" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Checklist</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog15;
